import { Box, type BoxProps } from '@mantine/core'
import { nprogress } from '@mantine/nprogress'
import clsx from 'clsx'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { type PropsWithChildren, useEffect } from 'react'

import { useLanguageState } from '@flecto/translations'
import { type CoreUser } from '@flecto/types'
import {
  ModalsProvider,
  DrawersProvider,
  MantineThemeProvider,
  type MantineThemeProviderProps,
} from '@flecto/ui'
import { fontFamily } from '@flecto/ui/src/themes/mantine/tokens'
import { isInsideIframe } from '@flecto/utils'

import { useStoreAnalytics } from '../../hooks/useStoreAnalytics'
import { useStoreBasket } from '../../hooks/useStoreBasket'
import { useStoreDetails } from '../../hooks/useStoreDetails'
import { useStoreLocalStorage } from '../../hooks/useStoreLocalStorage'
import { useStoreLogo } from '../../hooks/useStoreLogo'
import { useStoreUrl } from '../../hooks/useStoreUrl'
import { Footer } from './Footer'
import { Header } from './Header'
import { Section } from './Section'
import styles from './styles.module.css'

export type PageProps = PropsWithChildren<BoxProps> & {
  store?: CoreUser
  htmlTitle?: string
  htmlMetaDescription?: string
  enableRobots?: boolean
  schemaMarkup?: string
  htmlMetaOgTitle?: string
  htmlMetaOgDescription?: string
  withHeader?: boolean
  withFooter?: boolean
  withHeaderDates?: boolean
  withHeaderBasket?: boolean
  backgroundImage?: string
  hasCannonical?: boolean
}

function ThemeProvider({ children, themeOverride }: MantineThemeProviderProps) {
  return (
    <MantineThemeProvider themeOverride={themeOverride}>
      <ModalsProvider>
        <DrawersProvider>{children}</DrawersProvider>
      </ModalsProvider>
    </MantineThemeProvider>
  )
}

export function Page({
  children,
  htmlTitle = 'Flecto',
  htmlMetaDescription = 'Flecto Store',
  enableRobots = true,
  schemaMarkup,
  htmlMetaOgTitle = 'Flecto',
  htmlMetaOgDescription = 'Flecto Store',
  withHeader = true,
  withFooter = true,
  withHeaderDates = true,
  withHeaderBasket = true,
  hasCannonical = false,
  ...props
}: PageProps) {
  const router = useRouter()
  const { supportedLanguages } = useLanguageState()
  const isIframe = isInsideIframe()
  const store = useStoreDetails()
  const logo = useStoreLogo()
  const url = useStoreUrl()
  const { setStoreDomain, getBasketItems } = useStoreLocalStorage()
  const { setBasketItems } = useStoreBasket()
  const { track } = useStoreAnalytics()

  const faviconUrl = store?.logo_image_url

  const favicon = faviconUrl
    ? `${faviconUrl}/w=32,h=32,fit=cover`
    : '/favicon.png'

  const favicon2X = faviconUrl
    ? `${faviconUrl}/w=180,h=180,fit=cover`
    : '/favicon.png'

  useEffect(() => {
    if (!isIframe || typeof window === 'undefined') return

    const content = document.documentElement.querySelector(
      '.page-content'
    ) as HTMLDivElement

    const sendHeight = () => {
      setTimeout(() => {
        const height = content.scrollHeight + 20
        window.parent.postMessage({ iframeHeight: height }, '*')
      }, 100)
    }

    sendHeight()

    const observer = new ResizeObserver(sendHeight)
    observer.observe(content)

    router.events.on('routeChangeComplete', sendHeight)

    return () => {
      observer.disconnect()
      router.events.off('routeChangeComplete', sendHeight)
    }
  }, [router, isIframe])

  useEffect(() => {
    setStoreDomain(store?.domain)
    setBasketItems(getBasketItems())
  }, [])

  useEffect(() => {
    if (router) {
      router.events.on('routeChangeStart', () => nprogress.start())
      router.events.on('routeChangeComplete', () => nprogress.complete())

      return () => {
        router.events.off('routeChangeStart', () => nprogress.complete())
      }
    }
  }, [router])

  useEffect(() => {
    track(
      'PAGE__VIEW',
      {
        path: store?.apex_domain
          ? `${store?.apex_domain}${router?.asPath}`
          : `${store?.domain}${router?.asPath}`,
      },
      { excludeDates: true }
    )
  }, [router.asPath])

  const theme: MantineThemeProviderProps['themeOverride'] = {
    primaryColor: store?.style_main_color ?? 'dark',
    primaryShade: 7,
    fontFamily: store?.style_font_family ?? fontFamily,
    headings: {
      fontFamily: store?.style_font_family ?? fontFamily,
    },
  }

  const classes = clsx(styles.page, props.className, {
    [styles.isIframe]: isIframe,
  })

  return (
    <ThemeProvider themeOverride={theme}>
      <Head>
        <title>{htmlTitle}</title>
        <meta name='description' content={htmlMetaDescription} />
        <link rel='apple-touch-icon' sizes='180x180' href={favicon2X} />
        <link rel='icon' type='image/png' sizes='32x32' href={favicon} />
        <link rel='icon' type='image/png' sizes='16x16' href={favicon} />
        <meta
          name='robots'
          content={enableRobots ? 'index,follow' : 'noindex, nofollow'}
        />
        {hasCannonical && (
          <>
            <link rel='cannonical' href={url} />
            {supportedLanguages?.map((lang) => (
              <link
                key={lang?.locale}
                rel='alternate'
                hrefLang={lang?.locale}
                href={url}
              />
            ))}
            <link rel='alternate' hrefLang='x-default' href={url} />
          </>
        )}
        {schemaMarkup && (
          <script
            type='application/ld+json'
            dangerouslySetInnerHTML={{ __html: schemaMarkup }}
          />
        )}

        <meta property='og:image' content={logo ?? ''} />
        <meta
          property='og:url'
          content={
            store?.apex_domain
              ? `https://${store?.apex_domain}${router?.asPath}`
              : `https://${store?.domain}.flecto.store${router?.asPath}`
          }
        />
        <meta property='og:type' content='website' />
        <meta property='og:title' content={htmlMetaOgTitle ?? ''} />
        <meta property='og:description' content={htmlMetaOgDescription ?? ''} />
      </Head>
      <Box className={classes}>
        {withHeader && (
          <Header withDates={withHeaderDates} withBasket={withHeaderBasket} />
        )}
        <Box className={clsx(styles.content, 'page-content')}>{children}</Box>
        {withFooter && !isIframe && <Footer />}
      </Box>
    </ThemeProvider>
  )
}

Page.Section = Section
